import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import type { AuthChangeEvent, User } from '@supabase/supabase-js'
import { Lock, LogOut } from 'lucide-react'
import { supabase, supabasePublishableKey, supabaseUrl } from '../../lib/supabase'
import { PageTitle } from '../seo/page-title'
import { Button } from '../ui/button'

type AuthStatus = 'loading' | 'signed-out' | 'checking-admin' | 'authorized' | 'forbidden' | 'recovery' | 'reset-sent'

export interface AdminAuthGateProps {
  children: ReactNode
}

async function getAdminStatus(user: User | null) {
  if (!user) return false

  const { data, error } = await supabase
    .from('profiles')
    .select('is_admin')
    .eq('id', user.id)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data?.is_admin === true
}

export function AdminAuthGate({ children }: Readonly<AdminAuthGateProps>) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nextPassword, setNextPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const recoveryRef = useRef(false)

  const checkSession = useCallback(async () => {
    if (recoveryRef.current) {
      setStatus('recovery')
      return
    }
    setError(null)
    setStatus('loading')

    const { data, error: userError } = await supabase.auth.getUser()
    if (userError) {
      setStatus('signed-out')
      return
    }

    const user = data.user ?? null
    if (!user) {
      setStatus('signed-out')
      return
    }

    setStatus('checking-admin')
    try {
      const isAdmin = await getAdminStatus(user)
      setStatus(isAdmin ? 'authorized' : 'forbidden')
    } catch (adminError) {
      setError(adminError instanceof Error ? adminError.message : 'Unable to verify admin access')
      setStatus('forbidden')
    }
  }, [])

  useEffect(() => {
    const hashType = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('type')
    if (hashType === 'recovery') {
      recoveryRef.current = true
      setStatus('recovery')
    } else {
      void checkSession()
    }

    const { data } = supabase.auth.onAuthStateChange((event: AuthChangeEvent) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryRef.current = true
        setStatus('recovery')
        return
      }
      void checkSession()
    })

    return () => data.subscription.unsubscribe()
  }, [checkSession])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setStatus('loading')

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (signInError) {
      setError(signInError.message)
      setStatus('signed-out')
      return
    }

    await checkSession()
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    setStatus('signed-out')
  }

  async function handleResetRequest() {
    setError(null)
    if (!email.trim()) {
      setError('Enter the account email first.')
      return
    }
    const response = await fetch(`${supabaseUrl}/auth/v1/recover`, {
      method: 'POST',
      headers: {
        apikey: supabasePublishableKey,
        Authorization: `Bearer ${supabasePublishableKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        redirect_to: `${window.location.origin}/admin`,
      }),
    })
    if (!response.ok) {
      setError('Unable to send the password reset email.')
      return
    }
    setStatus('reset-sent')
  }

  async function handlePasswordUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    if (nextPassword.length < 8) {
      setError('Use at least 8 characters.')
      return
    }
    if (nextPassword !== confirmPassword) {
      setError('The passwords do not match.')
      return
    }
    const { error: updateError } = await supabase.auth.updateUser({ password: nextPassword })
    if (updateError) {
      setError(updateError.message)
      return
    }
    setNextPassword('')
    setConfirmPassword('')
    recoveryRef.current = false
    await checkSession()
  }

  if (status === 'authorized') {
    return <>{children}</>
  }

  return (
    <main className="admin-auth-screen">
      <PageTitle title="Admin Dashboard | AUTOGLOW Detailing" />
      <section className="admin-auth-card" aria-labelledby="admin-auth-heading">
        <Lock size={28} />
        <p className="eyebrow">Admin Access</p>
        <h1 id="admin-auth-heading">Autoglow Command Center</h1>

        {status === 'loading' || status === 'checking-admin' ? (
          <p className="admin-auth-muted">Verifying Supabase session...</p>
        ) : null}

        {status === 'forbidden' ? (
          <div className="admin-auth-message">
            <strong>Access denied</strong>
            <p>This account is signed in but is not marked as an admin in profiles.is_admin.</p>
            <Button type="button" onClick={handleSignOut}>
              <LogOut size={16} /> Sign Out
            </Button>
          </div>
        ) : null}

        {status === 'signed-out' ? (
          <form className="admin-auth-form" onSubmit={handleSubmit}>
            <label>
              Email
              <input
                autoComplete="email"
                name="email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </label>
            <label>
              Password
              <input
                autoComplete="current-password"
                name="password"
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </label>
            <Button type="submit">Sign In</Button>
            <Button type="button" onClick={() => { void handleResetRequest() }}>
              Email password reset
            </Button>
          </form>
        ) : null}

        {status === 'reset-sent' ? (
          <div className="admin-auth-message">
            <strong>Check your email</strong>
            <p>If this account can sign in, a password reset link is on its way. The current password stays active until a new one is saved.</p>
          </div>
        ) : null}

        {status === 'recovery' ? (
          <form className="admin-auth-form" onSubmit={handlePasswordUpdate}>
            <p className="admin-auth-muted">Choose a new admin password. The current password is still unchanged.</p>
            <label>
              New password
              <input
                autoComplete="new-password"
                minLength={8}
                name="nextPassword"
                onChange={(event) => setNextPassword(event.target.value)}
                required
                type="password"
                value={nextPassword}
              />
            </label>
            <label>
              Confirm password
              <input
                autoComplete="new-password"
                minLength={8}
                name="confirmPassword"
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                type="password"
                value={confirmPassword}
              />
            </label>
            <Button type="submit">Save new password</Button>
          </form>
        ) : null}

        {error ? <p className="admin-auth-error">{error}</p> : null}
      </section>
    </main>
  )
}
